import { taskApi } from '@api/index';
import * as taskActions from '@state/task/actions';
import { getId } from '@state/semester/selectors';
import { ManageTask, PageRange } from 'shovel-lib/types';
import { useDispatch, useSelector } from 'react-redux';
import { withAddedPages } from '@utils/taskPlanning';
import { ParsedPages } from './utils';

/**
 * Saving one task's estimate from outside the estimator dialog: the Plan Week dialog's unestimated
 * list and the pile's cards both do it, and both have to leave the stores in the same state the task
 * editor's save saga would.
 *
 * `afterSave` is for whatever the caller has to refetch afterwards — the dialog reloads the tasks in
 * its range; the pile relies on the reducers alone.
 */
export const useTaskEstimate = (afterSave?: () => Promise<void> | void) => {
  const dispatch = useDispatch();
  const semesterId = useSelector(getId);

  // The same read-then-patch the task editor autosaves with. The endpoint replaces every field of the task, and a
  // field left out of the body falls back to its default (an empty title), so the whole task goes back with it.
  // `added` puts the minutes on top of the time still needed instead of replacing it: the card's Add time, for a task
  // whose time ran out before the work did. Its Start ahead follows the new total too, as any change of time does
  // (Petr, 2026-10-22): the server sees the time change.
  const saveEstimate = async (taskId: number, minutes: number, added = false) => {
    const current = await taskApi.getManageTask(semesterId, taskId);
    const task = current.data as (ManageTask & { priority?: number }) | undefined;
    if (!current.ok || !task) return false;
    if (added) minutes += Math.max(task.timeNeed ?? 0, 0);
    const saved = await taskApi.saveManageTask(semesterId, taskId, {
      title: task.title,
      details: task.details,
      dueDate: task.dueDate,
      // Start ahead follows the estimate as the estimator's own endpoint has it follow (the student's days, or a day
      // per hour if that's more, unless they picked it) - worked out by the server from `startAheadFromEstimate`:
      // sent as a number from here, the PATCH took it as one the student picked, and pages left it alone after.
      daysToStartAhead: task.daysToStartAhead,
      startAheadFromEstimate: !added,
      duration: task.duration ?? 0,
      priority: task.priority ?? 0,
      timeNeed: minutes
    } as any);
    if (!saved.ok) return false;
    // Tell the stores what the task editor's save saga tells them. Without this the workload copy keeps the old
    // estimate, so the toolbar's "Estimate Tasks" count still lists a task this dialog just estimated.
    // shovel-lib types the PATCH as returning nothing; it answers with the updated task, which the reducers key off.
    const updated = (saved.data as unknown) as ({ taskId?: number } & Record<string, any>) | undefined;
    dispatch(
      taskActions.saveManageTask.success({
        taskId,
        request: { timeNeed: minutes } as any,
        response: (updated?.taskId ? updated : { taskId, timeNeed: minutes }) as any,
        task
      })
    );
    await afterSave?.();
    return true;
  };

  // Pages, not minutes: saving them lets the server work out the estimate at the reading source's own rate.
  // It also rewrites daysToStartAhead from that estimate, so the task can come back with an earlier start.
  const saveEstimatePages = async (taskId: number, pageRanges: PageRange[]) => {
    const saved = await taskApi.savePageRanges(semesterId, taskId, pageRanges);
    if (!saved.ok) return false;
    // Typed as returning nothing; it answers with the task, which is what the reducers key off.
    const updated = (saved.data as unknown) as { taskId?: number } | undefined;
    if (updated?.taskId) dispatch(taskActions.savePageRanges.success({ taskId, data: updated as any }));
    await afterSave?.();
    return true;
  };

  // A reading's +pages: the pages go in after the ones it has, as the task popup's Pages field adds them, so its time
  // still comes from its pages at the source's pace. Adding minutes left it needing more than its pages said.
  // `current` is what the popup already fetched to show, when it has it.
  const addPages = async (taskId: number, parsed: ParsedPages, current?: PageRange[]) => {
    const ranges = current ?? (await readingPageRanges(taskId));
    if (!ranges) return false;
    return saveEstimatePages(taskId, withAddedPages(ranges, parsed));
  };

  // The ranges a reading has, for +pages to show above its field (Petr, 2026-10-22).
  const readingPageRanges = async (taskId: number): Promise<PageRange[] | undefined> => {
    const current = await taskApi.getPageRanges(semesterId, taskId);
    return current.ok ? current.data ?? [] : undefined;
  };

  // A reading's pace in seconds a page, so a page count can show as time before the server has worked it out.
  const readingPace = async (taskId: number): Promise<number | undefined> => {
    const current = await taskApi.getManageTask(semesterId, taskId);
    const pace = (current.data as ManageTask | undefined)?.reading?.secondsPerPage;
    return current.ok && pace ? pace : undefined;
  };

  return { saveEstimate, saveEstimatePages, addPages, readingPageRanges, readingPace };
};
